import { createContext, useContext, useState, useEffect } from 'react';

const CurrencyContext = createContext();

export const CURRENCIES = {
  ILS: { symbol: '₪', name: 'Israeli Shekel', code: 'ILS' },
  EUR: { symbol: '€', name: 'Euro', code: 'EUR' },
  USD: { symbol: '$', name: 'US Dollar', code: 'USD' }
};

// Taux de conversion approximatifs (à remplacer par API en production)
const EXCHANGE_RATES = {
  EUR: { ILS: 4.0, USD: 1.1 },
  ILS: { EUR: 0.25, USD: 0.27 },
  USD: { EUR: 0.91, ILS: 3.7 }
};

export function CurrencyProvider({ children }) {
  const [currency, setCurrency] = useState(() => {
    const saved = localStorage.getItem('mutagim_currency');
    return saved || 'ILS';
  });

  useEffect(() => {
    localStorage.setItem('mutagim_currency', currency);
  }, [currency]);

  const formatPrice = (prices, showSymbol = true) => {
    if (!prices) return showSymbol ? `${CURRENCIES[currency].symbol}0` : '0';
    
    // Helper pour extraire la valeur numérique d'un prix
    const extractValue = (price) => {
      if (typeof price === 'number') return price;
      if (typeof price === 'object' && price !== null) {
        return price.amount || price.value || 0;
      }
      return 0;
    };
    
    // Si le prix pour la devise actuelle existe, l'utiliser directement
    if (prices[currency] !== undefined) {
      const price = extractValue(prices[currency]);
      const formatted = price.toLocaleString('fr-FR', { minimumFractionDigits: 0 });
      return showSymbol ? `${CURRENCIES[currency].symbol}${formatted}` : formatted;
    }
    
    // Sinon, trouver une devise disponible et convertir
    for (const [fromCurrency, amount] of Object.entries(prices)) {
      if (fromCurrency in CURRENCIES && amount !== undefined && amount !== null) {
        let convertedPrice = extractValue(amount);
        
        // Convertir si nécessaire
        if (fromCurrency !== currency && EXCHANGE_RATES[fromCurrency]) {
          const rate = EXCHANGE_RATES[fromCurrency][currency] || 1;
          convertedPrice = convertedPrice * rate;
        }
        
        const formatted = Math.round(convertedPrice).toLocaleString('fr-FR', { minimumFractionDigits: 0 });
        return showSymbol ? `${CURRENCIES[currency].symbol}${formatted}` : formatted;
      }
    }
    
    return showSymbol ? `${CURRENCIES[currency].symbol}0` : '0';
  };

  return (
    <CurrencyContext.Provider value={{ 
      currency, 
      setCurrency, 
      currencies: CURRENCIES,
      formatPrice 
    }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
}
